"use client"

import { useState } from "react"
import toast from "react-hot-toast"
import { UserGroupIcon, UserIcon, ShoppingCartIcon } from "@heroicons/react/24/outline"

import { Button } from "domains/layout/components/Button"
import { pushGtmEvent } from "lib/gtm"
import { useCart } from "domains/commerce/cartContext"
import { formatCurrencyDisplay } from "domains/commerce/utils/pricing"

export interface PriceOption {
    entryId: string
    amountMinor: number
    currency: string
    isFree: boolean
    stripePriceId?: string | null
}

interface Props {
    prices: {
        individual?: PriceOption
        organization?: PriceOption
    }
    video: {
        slug: string
        title: string
    }
}

export function PurchaseOptions({ prices, video }: Props) {
    const { addItem } = useCart()
    const [pendingId, setPendingId] = useState<string | null>(null)

    const trackClick = (type: "individual" | "organization") => {
        pushGtmEvent({
            event: "academy_add_to_cart",
            purchase_type: type,
            video_slug: video.slug,
            video_title: video.title,
        })
    }

    const handleAdd = async (option: PriceOption, variantLabel: string, type: "individual" | "organization") => {
        setPendingId(option.entryId)
        try {
            await addItem({
                priceEntryId: option.entryId,
                quantity: 1,
                title: video.title,
                productSlug: video.slug,
                variantName: variantLabel,
                currency: option.currency,
                amountMinor: option.amountMinor,
                isFree: option.isFree,
                stripePriceId: option.stripePriceId ?? null,
            })
            trackClick(type)
            toast.success("Added to cart")
        } catch (error) {
            console.error(error)
            toast.error((error as Error).message)
        } finally {
            setPendingId(null)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            {prices.individual && (
                <PurchaseCard
                    title="Individual Access"
                    description="Enjoy unlimited personal access to this video."
                    priceLabel={formatPrice(prices.individual)}
                    icon={UserIcon}
                    onAdd={() => handleAdd(prices.individual!, "Individual Access", "individual")}
                    disabled={pendingId === prices.individual.entryId}
                />
            )}
            {prices.organization && (
                <PurchaseCard
                    title="Organization Access"
                    description="Grant full access to your entire ResearchWiseAI team."
                    priceLabel={formatPrice(prices.organization)}
                    icon={UserGroupIcon}
                    onAdd={() => handleAdd(prices.organization!, "Organization Access", "organization")}
                    disabled={pendingId === prices.organization.entryId}
                />
            )}

            {!prices.individual && !prices.organization && (
                <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
                    <p>
                        Pricing unavailable. Please contact
                        {" "}
                        <a href="mailto:support@researchwiseai.com" className="text-blue-500 underline">
                            support@researchwiseai.com
                        </a>
                        .
                    </p>
                </div>
            )}

            <Button href="/academy/cart" intent="secondary" className="flex items-center justify-center gap-2">
                View Cart
                <ShoppingCartIcon className="size-5" />
            </Button>
        </div>
    )
}

interface PurchaseCardProps {
    title: string
    description: string
    priceLabel: string
    icon: typeof UserIcon
    onAdd: () => Promise<void>
    disabled: boolean
}

function PurchaseCard({ title, description, priceLabel, icon: Icon, onAdd, disabled }: PurchaseCardProps) {
    const normalizedTestId = `add-to-cart-${slugifyForTestId(title)}`

    return (
        <div className="flex flex-col justify-between rounded-lg border border-gray-200 p-4 shadow-sm dark:border-gray-700">
            <div>
                <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h2>
                <p className="text-sm font-semibold text-primary-900 dark:text-primary-200">{priceLabel}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{description}</p>
            </div>
            <Button
                data-testid={normalizedTestId}
                onClick={(event) => {
                    event.preventDefault()
                    void onAdd()
                }}
                className="mt-4 flex items-center justify-center gap-2"
                disabled={disabled}
            >
                Add to Cart
                <Icon className="size-5" />
            </Button>
        </div>
    )
}

function slugifyForTestId(label: string): string {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
}

function formatPrice(option: PriceOption): string {
    if (option.isFree || option.amountMinor === 0) {
        return "Free"
    }

    return formatCurrencyDisplay(option.amountMinor, option.currency)
}
